import { useState, useEffect } from 'react';
import { Plane, MapPin, Compass, Palmtree, Sun, Camera } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Navbar } from './Navbar';

const travelQuotes = [
  { es: "El mundo es un libro, y quienes no viajan leen solo una página.", en: "The world is a book, and those who do not travel read only one page.", pt: "O mundo é um livro, e quem não viaja lê apenas uma página." },
  { es: "Viajar es vivir.", en: "To travel is to live.", pt: "Viajar é viver." },
  { es: "La vida es corta y el mundo es grande.", en: "Life is short and the world is wide.", pt: "A vida é curta e o mundo é grande." },
  { es: "Aventuras te esperan.", en: "Adventures await you.", pt: "Aventuras te esperam." },
  { es: "Cada destino tiene su magia.", en: "Every destination has its magic.", pt: "Todo destino tem sua magia." },
  { es: "Descubre nuevos horizontes.", en: "Discover new horizons.", pt: "Descubra novos horizontes." },
  { es: "Tu próxima aventura comienza aquí.", en: "Your next adventure starts here.", pt: "Sua próxima aventura começa aqui." },
  { es: "Brasil te espera con los brazos abiertos.", en: "Brazil awaits you with open arms.", pt: "O Brasil te espera de braços abertos." },
];

const icons = [Plane, MapPin, Compass, Palmtree, Sun, Camera];

export function HotelDetailLoading() {
  const { i18n } = useTranslation();
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentQuoteIndex((prev) => (prev + 1) % travelQuotes.length);
        setIsTransitioning(false);
      }, 300);
    }, 3500);

    return () => clearInterval(interval);
  }, []);

  const getCurrentQuote = () => {
    const quote = travelQuotes[currentQuoteIndex];
    const lang = i18n.language;
    if (lang.startsWith('pt')) return quote.pt;
    if (lang.startsWith('en')) return quote.en;
    return quote.es;
  };

  const IconComponent = icons[currentQuoteIndex % icons.length];

  return (
    <>
      <Navbar />
      <div className="min-h-[80vh] flex flex-col items-center justify-center px-4 bg-gradient-to-br from-background via-background to-muted/30">
        {/* Animated Icon Container */}
        <div className="relative mb-8">
          {/* Outer ring */}
          <div className="absolute inset-0 rounded-full border-4 border-primary/20 animate-ping" style={{ animationDuration: '2s' }} />
          
          {/* Middle ring */}
          <div className="absolute inset-2 rounded-full border-2 border-primary/30 animate-pulse" />
          
          {/* Icon circle */}
          <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center shadow-lg border border-primary/20">
            <IconComponent 
              className={`w-10 h-10 text-primary transition-all duration-500 ${isTransitioning ? 'scale-75 opacity-0' : 'scale-100 opacity-100'}`} 
            />
          </div>

          {/* Floating decorative elements */}
          <div className="absolute -top-2 -right-2 w-4 h-4 bg-primary/30 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
          <div className="absolute -bottom-1 -left-3 w-3 h-3 bg-primary/20 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }} />
          <div className="absolute top-1/2 -right-4 w-2 h-2 bg-primary/40 rounded-full animate-bounce" style={{ animationDelay: '1s' }} />
        </div>

        {/* Quote Text */}
        <div className="text-center max-w-md">
          <p 
            className={`text-xl md:text-2xl font-medium text-foreground/80 italic transition-all duration-300 ${
              isTransitioning ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
            }`}
          >
            "{getCurrentQuote()}"
          </p>
        </div>

        {/* Loading indicator */}
        <div className="mt-10 flex items-center gap-2">
          <div className="flex gap-1">
            <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-6 w-48 h-1 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary/60 rounded-full animate-loading-progress" />
        </div>
      </div>
    </>
  );
}
